"use client";

import {
	Button,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	RichText,
	Text
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import type { HeadingTag } from "@/app/utilities/types";
import React from "react";

import { ConsultantDetails, ConsultantDetailsProps } from "./ConsultantDetails";
import { FindConsultant, FindConsultantProps } from "./FindConsultant";
import { FindParty, FindPartyProps } from "./FindParty";
import * as styles from "./FindPartyAndConsultant.module.scss";
import { PartyDetails, PartyDetailsProps } from "./PartyDetails";
import { PartyList, PartyListProps } from "./PartyList";

export interface FindPartyAndConsultantProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	headline?: string;
	headlineTag?: HeadingTag;
	partySelectedHeadline?: string;
	partySelectedHeadlineTag?: HeadingTag;
	consultantForm?: FindConsultantProps<ImageComponentProps>;
	partyForm?: FindPartyProps;
	partyDetails?: PartyDetailsProps<LinkComponentProps>;
	consultantDetails?: ConsultantDetailsProps;
	connectionInstruction?: string;
	partyConnectionInstruction?: string;
	partiesList?: PartyListProps;
	notActiveInfo?: {
		description?: string;
		modal?: React.ReactNode;
	};
}

/**
 * FindPartyAndConsultant Component
 */
export function FindPartyAndConsultant<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	headline,
	headlineTag = "h1",
	partySelectedHeadline,
	partySelectedHeadlineTag = "h3",
	consultantForm = {
		googleMapsApiKey: ""
	},
	partyForm = {},
	partyDetails = {},
	consultantDetails = {},
	connectionInstruction,
	partyConnectionInstruction,
	partiesList = {},
	notActiveInfo = {}
}: FindPartyAndConsultantProps<
	LinkComponentProps,
	ImageComponentProps
>): JSX.Element {
	const hasParty = Object.keys(partyDetails || {}).length;
	const hasConsultant = Object.keys(consultantDetails || {}).length;
	const hasPartiesList = partiesList?.items?.length;
	const isActiveConsultant = !Object.keys(notActiveInfo).length;

	const IS_NOT_CONNECTED = !hasConsultant && !hasParty;
	const IS_PARTY_CONNECTED =
		hasParty && !hasPartiesList && isActiveConsultant;
	const IS_CONSULTANT_CONNECTED_AND_NOT_PARTY =
		hasConsultant && isActiveConsultant && hasPartiesList;
	const IS_CONSULTANT_CONNECTED_BUT_NO_ACTIVE = !isActiveConsultant;

	return (
		<div className={styles["find-party-consultant"]}>
			{IS_NOT_CONNECTED ? (
				<div className="grid">
					<div
						{...classNameHelper(
							"col-xs-12 col-lg-6",
							styles["find-form-col"]
						)}
					>
						<FindParty {...partyForm} />
					</div>
					<div
						{...classNameHelper(
							"col-xs-12 col-lg-6",
							styles["find-form-col"]
						)}
					>
						<FindConsultant {...consultantForm} />
					</div>
				</div>
			) : null}

			{IS_PARTY_CONNECTED ? (
				<section>
					<Heading
						className={styles["find-party-consultant__title"]}
						styledAs="h1"
						tag={headlineTag}
					>
						{headline}
					</Heading>

					<PartyDetails<LinkComponentProps> {...partyDetails} />

					<Text
						styledAs="mdr"
						className={styles["find-party-consultant__instruction"]}
					>
						{partyConnectionInstruction}
					</Text>

					<ConsultantDetails {...consultantDetails} />

					<RichText
						className={styles["find-party-consultant__instruction"]}
						content={connectionInstruction || ""}
					/>

					{/* Shopping list removed as per requirements */}
				</section>
			) : null}

			{IS_CONSULTANT_CONNECTED_AND_NOT_PARTY ? (
				<section>
					<Heading
						className={styles["find-party-consultant__title"]}
						styledAs="h1"
						tag={headlineTag}
					>
						{headline}
					</Heading>

					<ConsultantDetails {...consultantDetails} />

					<RichText
						className={styles["find-party-consultant__instruction"]}
						content={connectionInstruction || ""}
					/>

					{hasParty ? (
						<div
							className={
								styles["find-party-consultant__selected-party"]
							}
						>
							<Heading
								className={
									styles["find-party-consultant__title"]
								}
								styledAs="h1"
								tag={partySelectedHeadlineTag}
							>
								{partySelectedHeadline}
							</Heading>

							<PartyDetails<LinkComponentProps>
								{...partyDetails}
							/>

							<Button
								kind="tertiary"
								size="lg"
								surface="default"
								onClick={() =>
									partiesList?.onListSelect?.(null)
								}
							>
								Switch to another open party
							</Button>
						</div>
					) : null}

					{!hasParty && hasPartiesList ? (
						<PartyList {...partiesList} />
					) : null}

					{!hasPartiesList ? (
						<div
							className={`grid ${styles["find-party-consultant__form"]}`}
						>
							<div className="col-xs-12 col-lg-6">
								<FindParty {...partyForm} />
							</div>
							<div className="col-xs-12 col-lg-6">
								<FindConsultant {...consultantForm} />
							</div>
						</div>
					) : null}

					{/* Shopping list removed as per requirements */}
				</section>
			) : null}

			{IS_CONSULTANT_CONNECTED_BUT_NO_ACTIVE ? (
				<section>
					<Heading
						className={styles["find-party-consultant__title"]}
						styledAs="h1"
						tag={headlineTag}
					>
						{headline}
					</Heading>

					<div
						className={styles["find-party-consultant__not-active"]}
					>
						<RichText
							className={
								styles[
									"find-party-consultant__not-active__description"
								]
							}
							content={notActiveInfo?.description || ""}
						/>

						{notActiveInfo?.modal}
					</div>

					{/* Shopping list removed as per requirements */}
				</section>
			) : null}
		</div>
	);
}
